import * as RadixToast from '@radix-ui/react-toast';
import { CircleCheck, CircleX, Info, LoaderCircle, TriangleAlert, X } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { Button } from '@/shared/ui/Button';

import styles from './Toast.module.scss';
import { toast, useToasts, type ToastRecord, type ToastTone } from './toastStore';

/**
 * Показ тостов поверх примитива Radix.
 *
 * Своими руками тост выглядит как коробка в углу, а на деле это область
 * `aria-live` с правильной настойчивостью, пауза таймера при наведении и при
 * получении фокуса, горячая клавиша F8 для перехода к уведомлениям с
 * клавиатуры, смахивание пальцем и возврат фокуса туда, где он был. Ничего
 * из этого не видно глазами, и всё это ломается незаметно.
 */

const TONE: Record<ToastTone, string> = {
  success: styles['toast--success'] ?? '',
  error: styles['toast--error'] ?? '',
  warning: styles['toast--warning'] ?? '',
  info: styles['toast--info'] ?? '',
  loading: styles['toast--loading'] ?? '',
};

const ICON: Record<ToastTone, ReactNode> = {
  success: <CircleCheck size={17} />,
  error: <CircleX size={17} />,
  warning: <TriangleAlert size={17} />,
  info: <Info size={17} />,
  loading: <LoaderCircle size={17} className={styles['toast__spinner']} />,
};

function ToastItem({ record }: { record: ToastRecord }) {
  const [paused, setPaused] = useState(false);

  const limited = Number.isFinite(record.duration);

  // Полоска времени рисуется только у тостов, которые действительно уходят
  // сами. У ошибки она означала бы обратное тому, что происходит.
  const life = limited
    ? ({ '--toast-life': `${String(record.duration)}ms` } as CSSProperties)
    : undefined;

  return (
    <RadixToast.Root
      className={cx(styles['toast'], TONE[record.tone])}
      open={record.open}
      duration={record.duration}
      // Ошибку и тост с кнопкой скринридер зачитывает немедленно, прервав
      // текущее чтение: они требуют решения. Успех ждёт своей очереди —
      // перебивать человека ради «Сохранено» невежливо.
      type={record.tone === 'error' || record.action !== undefined ? 'foreground' : 'background'}
      onOpenChange={(open) => {
        if (!open) {
          toast.dismiss(record.id);
        }
      }}
      onPause={() => {
        setPaused(true);
      }}
      onResume={() => {
        setPaused(false);
      }}
      data-paused={paused ? '' : undefined}
      style={life}
    >
      <span className={styles['toast__icon']} aria-hidden="true">
        {ICON[record.tone]}
      </span>

      <div className={styles['toast__text']}>
        <RadixToast.Title className={styles['toast__title']}>{record.title}</RadixToast.Title>

        {record.description !== undefined && (
          <RadixToast.Description className={styles['toast__description']}>
            {record.description}
          </RadixToast.Description>
        )}
      </div>

      {record.action !== undefined && (
        <RadixToast.Action asChild altText={record.action.altText ?? record.action.label}>
          <Button variant="outline" tone="neutral" size="sm" onClick={record.action.onClick}>
            {record.action.label}
          </Button>
        </RadixToast.Action>
      )}

      <RadixToast.Close className={styles['toast__close']} aria-label="Закрыть">
        <X size={15} />
      </RadixToast.Close>

      {limited && <span className={styles['toast__life']} aria-hidden="true" />}
    </RadixToast.Root>
  );
}

/**
 * Область показа тостов. Ставится один раз рядом с корнем приложения.
 *
 * Отдельного провайдера с контекстом здесь нет: очередь живёт в модуле, и
 * `toast.success(...)` работает из любого места, включая перехватчик ошибок
 * API, который про React ничего не знает.
 */
export function Toaster() {
  const records = useToasts();

  return (
    <RadixToast.Provider swipeDirection="right" label="Уведомление">
      {records.map((record) => (
        <ToastItem key={record.id} record={record} />
      ))}

      <RadixToast.Viewport className={styles['viewport']} label="Уведомления ({hotkey})" />
    </RadixToast.Provider>
  );
}
